import * as msal from "@azure/msal-browser";
import { BrowserAuthError, InteractionRequiredAuthError } from "@azure/msal-browser";

import { ConfigApp } from "../configApp";
import MsalFactory from "../factories/msalFactory";

let redirectInProgress = false;

const MSAL_INTERACTION_STATUS_KEY = "msal.interaction.status";

export class MsalAuthenticator {
  protected async signInAsync(): Promise<msal.AccountInfo | null> {
    try {
      const account = await this.checkCacheForExistingAccountAsync();
      if (account == null) {
        return MsalFactory.getMsalInstance()
          .loginRedirect({ scopes: ConfigApp.scopes.app })
          .then(() => {
            return null;
          })
          .catch((error) => {
            return null;
          });
      } else {
        return account;
      }
    } catch (error) {
      // More about AAD error codes at https://docs.microsoft.com/en-us/azure/active-directory/develop/reference-aadsts-error-codes
      console.error(error);

      throw error;
    }
  }

  protected async acquireTokenAsync(scopes: string[]): Promise<string | null> {
    const account = this.getLoggedInAccount();
    if (account == null) {
      return null;
    }

    try {
      const silentRequest = {
        account: account,
        scopes: scopes,
        forceRefresh: false,
      };

      const result = await MsalFactory.getMsalInstance().acquireTokenSilent(silentRequest);

      if (result == null || !result.accessToken) {
        throw Error();
      }

      return result.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError && !redirectInProgress) {
        redirectInProgress = true;
        const redirectRequest = {
          account: account,
          scopes: scopes,
          forceRefresh: false,
        };
        try {
          await MsalFactory.getMsalInstance().acquireTokenRedirect(redirectRequest);
        } catch (redirectError) {
          if (redirectError instanceof BrowserAuthError && redirectError.errorCode === "interaction_in_progress") {
            localStorage.removeItem(MSAL_INTERACTION_STATUS_KEY);
            try {
              await MsalFactory.getMsalInstance().acquireTokenRedirect(redirectRequest);
            } catch {}
          }
        }
      }
      return null;
    }
  }

  private async checkCacheForExistingAccountAsync(): Promise<msal.AccountInfo | null> {
    return MsalFactory.getMsalInstance()
      .handleRedirectPromise()
      .then((response) => {
        if (response != null) {
          return response.account;
        } else {
          // todo: check token expiration, return null if token is expired. (get exp claim from idToken)
          return this.getLoggedInAccount();
        }
      });
  }

  private getLoggedInAccount(): msal.AccountInfo | null {
    /**
     * See here for more info on account retrieval:
     * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-common/docs/Accounts.md
     */
    const allAccounts = MsalFactory.getMsalInstance().getAllAccounts();
    if (allAccounts.length === 1) {
      return allAccounts[0];
    } else if (allAccounts.length > 1) {
      MsalFactory.logoutAllAccounts(null);
    }

    return null;
  }
}
